import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Network, RefreshCw, ArrowRight, Cloud as CloudIcon, Filter, Info,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

// ── Types (lean copy of Service shape) ───────────────────────────────────────

interface Service {
  id: number
  name: string
  description: string
  tier: 1 | 2 | 3
  kind: string
  cloud: string
  region: string
  dependencies: string[]
  status: string
  owner: string
}

const CLOUD_COLOR: Record<string, string> = {
  aws:     '#ff9900',
  azure:   '#0078d4',
  gcp:     '#4285f4',
  hetzner: '#d50c2d',
  digitalocean: '#0069ff',
  cloudflare: '#f48120',
  'on-prem': '#71717a',
  '': '#52525b',
}

const TIER_BORDER: Record<number, string> = {
  1: '#f43f5e',  // rose
  2: '#f59e0b',  // amber
  3: '#10b981',  // emerald
}

const TIER_RADIUS: Record<number, number> = { 1: 22, 2: 18, 3: 14 }

// ── Layout: group services into vertical columns by cloud ────────────────────

interface NodePos { service: Service; x: number; y: number }

function layout(services: Service[], width: number, padding = 60): { nodes: NodePos[]; columns: { cloud: string; x: number; w: number }[]; height: number } {
  // Group by cloud (preserve insertion order, '' last)
  const byCloud = new Map<string, Service[]>()
  for (const s of services) {
    const k = s.cloud || 'other'
    if (!byCloud.has(k)) byCloud.set(k, [])
    byCloud.get(k)!.push(s)
  }

  const cloudKeys = Array.from(byCloud.keys()).sort((a, b) => {
    if (a === 'other') return 1
    if (b === 'other') return -1
    return a.localeCompare(b)
  })

  const cols = Math.max(cloudKeys.length, 1)
  const innerW = Math.max(width - padding * 2, 400)
  const colW = innerW / cols

  const rowH = 80
  const nodes: NodePos[] = []
  const columns: { cloud: string; x: number; w: number }[] = []
  let maxRows = 0

  cloudKeys.forEach((cloud, ci) => {
    const xCenter = padding + colW * ci + colW / 2
    columns.push({ cloud, x: padding + colW * ci, w: colW })
    const list = byCloud.get(cloud)!
    list.sort((a, b) => a.tier - b.tier || a.name.localeCompare(b.name))
    list.forEach((svc, idx) => {
      nodes.push({ service: svc, x: xCenter, y: padding + rowH * idx + rowH / 2 })
    })
    maxRows = Math.max(maxRows, list.length)
  })

  const height = padding * 2 + rowH * Math.max(maxRows, 1)
  return { nodes, columns, height }
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function ServiceGraphPage() {
  const navigate = useNavigate()
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [hovered, setHovered] = useState<number | null>(null)
  const [tierFilter, setTierFilter] = useState<number | null>(null)
  const [width, setWidth] = useState(1100)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/services')
      setServices(data.services)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Track container width responsively
  useEffect(() => {
    const onResize = () => {
      const el = document.getElementById('service-graph-container')
      if (el) setWidth(el.clientWidth)
    }
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const filtered = useMemo(
    () => services.filter(s => (tierFilter === null || s.tier === tierFilter) && s.status !== 'retired'),
    [services, tierFilter]
  )

  const { nodes, columns, height } = useMemo(() => layout(filtered, width), [filtered, width])

  // Build name → node map for edge lookup
  const byName = useMemo(() => {
    const m = new Map<string, NodePos>()
    for (const n of nodes) m.set(n.service.name, n)
    return m
  }, [nodes])

  const edges = useMemo(() => {
    const out: { from: NodePos; to: NodePos }[] = []
    for (const n of nodes) {
      for (const dep of n.service.dependencies || []) {
        const target = byName.get(dep)
        if (target) out.push({ from: n, to: target })
      }
    }
    return out
  }, [nodes, byName])

  // Reverse adjacency for highlighting
  const incoming = useMemo(() => {
    const m = new Map<number, Set<number>>()
    for (const e of edges) {
      if (!m.has(e.to.service.id)) m.set(e.to.service.id, new Set())
      m.get(e.to.service.id)!.add(e.from.service.id)
    }
    return m
  }, [edges])

  const outgoing = useMemo(() => {
    const m = new Map<number, Set<number>>()
    for (const e of edges) {
      if (!m.has(e.from.service.id)) m.set(e.from.service.id, new Set())
      m.get(e.from.service.id)!.add(e.to.service.id)
    }
    return m
  }, [edges])

  const isHighlighted = (id: number): boolean => {
    if (hovered === null) return false
    if (hovered === id) return true
    return (outgoing.get(hovered)?.has(id) ?? false) || (incoming.get(hovered)?.has(id) ?? false)
  }

  if (loading && services.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }

  if (services.length === 0) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
          <Network size={20} className="text-blue" /> Service Dependency Graph
        </h1>
        <div className="card p-8 text-center text-text-muted text-sm">
          No services in the catalog yet. Add some at <button type="button" className="text-blue-light hover:underline" onClick={() => navigate('/app/services')}>/app/services</button>.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Network size={20} className="text-blue" /> Service Dependency Graph
          </h1>
          <p className="text-[10px] text-text-secondary">
            {nodes.length} services across {columns.length} cloud{columns.length !== 1 ? 's' : ''} ·
            {' '}{edges.length} declared dependencies. Hover a node to highlight its blast radius.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={12} className="text-text-muted" />
          <button type="button" onClick={() => setTierFilter(null)}
            className={clsx('px-2 py-1 text-[10px] rounded border transition-colors',
              tierFilter === null
                ? 'bg-blue/10 border-blue/30 text-blue-light'
                : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
            )}>All</button>
          {[1, 2, 3].map(t => (
            <button type="button" key={t} onClick={() => setTierFilter(t)}
              className={clsx('px-2 py-1 text-[10px] rounded border transition-colors',
                tierFilter === t
                  ? 'bg-blue/10 border-blue/30 text-blue-light'
                  : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
              )}>T{t}</button>
          ))}
          <button type="button" onClick={fetchData} className="btn-ghost text-[11px]" title="Refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Graph */}
      <div id="service-graph-container" className="card p-2 overflow-x-auto">
        <svg width={width} height={height} className="block">
          {/* Cloud column backdrops */}
          {columns.map(c => (
            <g key={c.cloud}>
              <rect
                x={c.x + 6}
                y={20}
                width={c.w - 12}
                height={height - 30}
                rx={8}
                fill="#18181c"
                stroke={CLOUD_COLOR[c.cloud] || '#3f3f46'}
                strokeOpacity={0.2}
                strokeWidth={1}
              />
              <text x={c.x + c.w / 2} y={36} textAnchor="middle"
                fill={CLOUD_COLOR[c.cloud] || '#a1a1aa'}
                fontSize={11} fontWeight={700}
                style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
              >
                {c.cloud === 'other' ? '— uncategorised —' : c.cloud}
              </text>
            </g>
          ))}

          {/* Edges */}
          <defs>
            <marker id="arrow" viewBox="0 -5 10 10" refX="20" refY="0" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,-5L10,0L0,5" fill="#52525b" />
            </marker>
            <marker id="arrow-hl" viewBox="0 -5 10 10" refX="20" refY="0" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,-5L10,0L0,5" fill="#3b82f6" />
            </marker>
          </defs>
          {edges.map((e, i) => {
            const hl = hovered !== null &&
              (e.from.service.id === hovered || e.to.service.id === hovered)
            return (
              <line
                key={i}
                x1={e.from.x}
                y1={e.from.y}
                x2={e.to.x}
                y2={e.to.y}
                stroke={hl ? '#3b82f6' : '#3f3f46'}
                strokeWidth={hl ? 2 : 1}
                strokeOpacity={hovered !== null && !hl ? 0.15 : 0.6}
                markerEnd={`url(#${hl ? 'arrow-hl' : 'arrow'})`}
              />
            )
          })}

          {/* Nodes */}
          {nodes.map(n => {
            const s = n.service
            const r = TIER_RADIUS[s.tier]
            const fill = CLOUD_COLOR[s.cloud] || '#52525b'
            const stroke = TIER_BORDER[s.tier]
            const dim = hovered !== null && !isHighlighted(s.id)
            return (
              <g key={s.id}
                style={{ cursor: 'pointer', opacity: dim ? 0.25 : 1, transition: 'opacity 120ms' }}
                onMouseEnter={() => setHovered(s.id)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => navigate('/app/services')}
              >
                <circle cx={n.x} cy={n.y} r={r + 4} fill={stroke} fillOpacity={0.15} />
                <circle cx={n.x} cy={n.y} r={r} fill={fill} fillOpacity={0.85} stroke={stroke} strokeWidth={2} />
                <text x={n.x} y={n.y + 4} textAnchor="middle"
                  fill="#fff" fontSize={10} fontWeight={700}>
                  T{s.tier}
                </text>
                <text x={n.x} y={n.y + r + 14} textAnchor="middle"
                  fill="#e4e4e7" fontSize={10} fontWeight={600}>
                  {s.name}
                </text>
                <text x={n.x} y={n.y + r + 26} textAnchor="middle"
                  fill="#a1a1aa" fontSize={9}>
                  {s.kind}{s.region ? ` · ${s.region}` : ''}
                </text>
              </g>
            )
          })}
        </svg>
      </div>

      {/* Legend */}
      <div className="card p-3 flex flex-wrap items-center gap-4 text-[10px]">
        <div className="flex items-center gap-2">
          <span className="uppercase tracking-wider text-text-muted font-semibold">Tier border:</span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full border-2 border-rose" /> T1 critical
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full border-2 border-amber" /> T2 important
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full border-2 border-emerald" /> T3 supporting
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="uppercase tracking-wider text-text-muted font-semibold">Fill:</span>
          {Object.entries(CLOUD_COLOR).filter(([k]) => k && k !== 'other').slice(0, 6).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: v }} />
              <span className="text-text-secondary">{k}</span>
            </span>
          ))}
        </div>
        <span className="ml-auto text-text-muted flex items-center gap-1">
          <Info size={10} />
          Click a node to open the catalog · arrows point from dependent → dependency
        </span>
      </div>
    </div>
  )
}
